import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Icon, IconButton, Logo, Tooltip } from '@/ds';
import { LOGO_MARK } from '@/ds/assets';
import { NAV_ITEMS, type NavItem } from '@/lib/nav';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useIconButtonSize } from '@/lib/useControlSize';
import type { CurrentUser } from '@/api/hooks';
import { MoneySummary } from './MoneySummary';

/** Каркас из design-system/ui_kits/webapp/AppShell.jsx: сайдбар 264, топбар 64. */

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

function SidebarItem({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? 'page' : undefined}
      className="kj-nav-item"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '0 12px',
        border: 'none',
        cursor: 'pointer',
        borderRadius: 'var(--radius-md)',
        textAlign: 'left',
        background: active ? 'var(--surface-brand-soft)' : hover ? 'var(--ink-50)' : 'transparent',
        color: active ? 'var(--burgundy-800)' : 'var(--text-body)',
        font: `${active ? '500' : '400'} 14px var(--font-text)`,
        transition: 'var(--transition-control)',
      }}
    >
      <Icon name={item.icon} size={18} color={active ? 'var(--burgundy-700)' : 'var(--ink-500)'} />
      {item.label}
    </button>
  );
}

function SidebarBody({
  currentPath,
  onNavigate,
  user,
  onLogout,
  loggingOut,
  iconSize,
}: {
  currentPath: string;
  onNavigate: (path: string) => void;
  user: CurrentUser;
  onLogout: () => void;
  loggingOut: boolean;
  iconSize: 'sm' | 'md' | 'lg';
}) {
  return (
    <>
      <div style={{ padding: '0 4px' }}>
        <Logo size={26} src={LOGO_MARK} />
      </div>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            active={
              item.path === '/' ? currentPath === '/' : currentPath.startsWith(item.path)
            }
            onClick={() => onNavigate(item.path)}
          />
        ))}
      </nav>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <MoneySummary enabled />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
          <span
            style={{
              width: 32,
              height: 32,
              flex: '0 0 32px',
              borderRadius: 999,
              background: 'var(--burgundy-700)',
              color: 'var(--white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: '500 13px var(--font-text)',
            }}
          >
            {initials(user.name)}
          </span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1.3,
              minWidth: 0,
            }}
          >
            <span
              style={{
                font: '500 13px var(--font-text)',
                color: 'var(--text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.name}
            </span>
            <span
              style={{
                font: '400 12px var(--font-text)',
                color: 'var(--text-subtle)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </span>
          </span>
          <span style={{ marginLeft: 'auto' }}>
            <Tooltip label="Выйти" side="top">
              <IconButton label="Выйти" size={iconSize} onClick={onLogout} disabled={loggingOut}>
                <Icon name="LogOut" size={16} />
              </IconButton>
            </Tooltip>
          </span>
        </div>
      </div>
    </>
  );
}

export function AppShell({
  title,
  subtitle,
  unreadCount,
  onRefresh,
  refreshing,
  user,
  onLogout,
  loggingOut,
  children,
}: {
  title: string;
  subtitle?: string;
  unreadCount: number;
  onRefresh: () => void;
  refreshing: boolean;
  user: CurrentUser;
  onLogout: () => void;
  loggingOut: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const iconButtonSize = useIconButtonSize();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const go = (path: string) => navigate(path);

  return (
    <div className="kj-shell">
      <aside className="kj-sidebar">
        <SidebarBody
          currentPath={location.pathname}
          onNavigate={go}
          user={user}
          onLogout={onLogout}
          loggingOut={loggingOut}
          iconSize="sm"
        />
      </aside>

      {isMobile && drawerOpen ? (
        <>
          <div
            className="kj-drawer-scrim"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          />
          <aside className="kj-drawer" aria-label="Меню">
            <SidebarBody
              currentPath={location.pathname}
              onNavigate={go}
              user={user}
              onLogout={onLogout}
              loggingOut={loggingOut}
              iconSize={iconButtonSize}
            />
          </aside>
        </>
      ) : null}

      <main className="kj-main">
        <header className="kj-topbar">
          <span className="kj-menu-button">
            <IconButton label="Меню" size={iconButtonSize} onClick={() => setDrawerOpen(true)}>
              <Icon name="Menu" size={18} />
            </IconButton>
          </span>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1.25,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            <span
              style={{
                font: '500 18px var(--font-display)',
                letterSpacing: 'var(--ls-heading)',
                color: 'var(--text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </span>
            {subtitle ? (
              <span
                style={{
                  font: '400 12px var(--font-text)',
                  color: 'var(--text-subtle)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </span>
            ) : null}
          </div>

          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flex: '0 0 auto',
            }}
          >
            {isMobile ? (
              <Tooltip label="Обновить">
                <IconButton label="Обновить" size={iconButtonSize} onClick={onRefresh} disabled={refreshing}>
                  <Icon name="RefreshCw" size={18} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={onRefresh}
                loading={refreshing}
                iconLeft={<Icon name="RefreshCw" size={16} />}
              >
                Обновить
              </Button>
            )}

            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Tooltip label="Уведомления">
                <IconButton label="Уведомления" size={iconButtonSize} onClick={() => go('/notifications')}>
                  <Icon name="Bell" size={18} />
                </IconButton>
              </Tooltip>
              {unreadCount > 0 ? (
                <span style={{ position: 'absolute', top: -4, right: -6 }}>
                  <Badge tone="solid">{unreadCount}</Badge>
                </span>
              ) : null}
            </span>
          </div>
        </header>

        <div className="kj-content">{children}</div>
      </main>
    </div>
  );
}
